import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import CreatePaymentForm from '../components/CreatePaymentForm';
import EditPaymentForm from '../components/EditPaymentForm';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiX, FiSearch } from 'react-icons/fi';

const PaymentPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [, setTotalPayments] = useState(0);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // Search functionality state
    const [searchType, setSearchType] = useState('date');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    const fetchPayments = useCallback(async (page) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/payments?page=${page}&limit=20&sort=createdAt`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setPayments(response.data.data);
                setTotalPages(response.data.totalPages);
                setTotalPayments(response.data.total);
                
                // If it's the initial load, set the current page to the last page
                if (isInitialLoad) {
                    setCurrentPage(response.data.totalPages);
                    setIsInitialLoad(false);
                }
            }
        } catch (err) {
            setError(`Failed to fetch payments: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    }, [isInitialLoad]);

    useEffect(() => {
        fetchPayments(currentPage);
    }, [currentPage, fetchPayments]);

    // Open create form when navigated with ?open=create (e.g. from Layout action bar)
    useEffect(() => {
        if (searchParams.get('open') === 'create') {
            setShowCreateForm(true);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleCreateSuccess = (newPayment) => {
        setShowCreateForm(false);
        fetchPayments(currentPage);
    };

    // Search functionality
    const handleSearch = async () => {
        if (!searchTerm.trim()) return;
        
        setIsSearching(true);
        setError('');
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/payments?searchType=${searchType}&searchTerm=${searchTerm}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                console.log('Search results:', response.data.data);
                setSearchResults(response.data.data);
                setHasSearched(true);
            }
        } catch (err) {
            setError(`Search failed: ${err.response?.data?.error || err.message}`);
        } finally {
            setIsSearching(false);
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSearchResults([]);
        setHasSearched(false);
        setError('');
        fetchPayments(currentPage); // Reload original data
    };

    const handleSearchTypeChange = (e) => {
        setSearchType(e.target.value);
        setSearchTerm(''); // Clear search term when changing type
        if (hasSearched) {
            clearSearch();
        }
    };

    const handleEditSuccess = (updatedPayment) => {
        setShowEditModal(false);
        setSelectedPayment(null);
        fetchPayments(currentPage);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const formatAmount = (amount) => {
        if (!amount) return 'N/A';
        return `$${amount.toFixed(2)}`;
    };

    const handleView = (payment) => {
        setSelectedPayment(payment);
        setShowViewModal(true);
    };

    const handleEdit = (payment) => {
        setSelectedPayment(payment);
        setShowEditModal(true);
    };

    const handleDelete = (payment) => {
        setSelectedPayment(payment);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/payments/${selectedPayment._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedPayment(null);
            fetchPayments(currentPage);
        } catch (err) {
            setError(`Failed to delete payment: ${err.response?.data?.error || err.message}`);
        }
    };

    const getPaymentTypeColor = (type) => {
        switch (type) {
            case 'individual':
                return 'bg-blue-100 text-blue-800';
            case 'group':
                return 'bg-green-100 text-green-800';
            case 'add_on':
                return 'bg-yellow-100 text-yellow-800';
            case 'misc_fees':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatPaymentType = (type) => {
        if (type === 'misc_fees') return 'Misc Fees';
        if (type === 'add_on') return 'Add-on';
        return type;
    };

    const getEligibleMembers = (payment, participants) => {
        if (!participants || !Array.isArray(participants)) return [];
        const paymentDate = new Date(payment.paymentDate);
        
        // For BOTH add_on AND group payments, only show members who joined on the payment date
        return participants.filter(participant => {
            if (!participant || !participant.memberID || !participant.joinDate) return false;
            const joinDate = new Date(participant.joinDate);
            return joinDate.toDateString() === paymentDate.toDateString();
        });
    };

    return (
        <Layout>
            <div className="container mx-auto w-full max-w-none px-8 py-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Payments</h1>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                    >
                        <FiPlus /> New Payment
                    </button>
                </div>

                {/* Search Interface */}
                <div className="bg-white p-4 rounded-lg shadow mb-6">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Search by:</label>
                            <select 
                                value={searchType} 
                                onChange={handleSearchTypeChange}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="date">Date</option>
                                <option value="godid">God ID</option>
                            </select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <input
                                type={searchType === 'date' ? 'date' : 'text'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={searchType === 'date' ? 'Select date' : 'Enter God ID'}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                            />
                            
                            <button 
                                onClick={handleSearch}
                                disabled={!searchTerm.trim() || isSearching}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <FiSearch className="w-4 h-4" />
                                {isSearching ? 'Searching...' : 'Search'}
                            </button>
                            
                            {hasSearched && (
                                <button 
                                    onClick={clearSearch}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center gap-2"
                                >
                                    <FiX className="w-4 h-4" />
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                    
                    {/* Search Results Summary */}
                    {hasSearched && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-md">
                            <p className="text-blue-800 text-sm">
                                Search Results: {searchResults.length} payment(s) found for {searchType === 'date' ? `date ${searchTerm}` : `God ID ${searchTerm}`}
                            </p>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {showCreateForm && (
                    <CreatePaymentForm
                        onSuccess={handleCreateSuccess}
                        onCancel={() => setShowCreateForm(false)}
                    />
                )}

                {showEditModal && selectedPayment && (
                    <EditPaymentForm
                        payment={selectedPayment}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedPayment(null);
                        }}
                    />
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && selectedPayment && (
                    <DeleteConfirmationModal
                        isOpen={showDeleteConfirm}
                        onConfirm={confirmDelete}
                        onCancel={() => setShowDeleteConfirm(false)}
                    />
                )}

                {loading ? (
                    <div className="text-center py-4">Loading...</div>
                ) : (
                    <>
                        <div className="bg-white rounded-lg shadow overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Payment Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Member/Group
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Payment Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Renewal Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {(hasSearched ? searchResults : payments).map((payment) => {
                                        // Safely access participants data for both normal and search results
                                        let participants = [];
                                        if (payment.godGroup && payment.godGroup.groupMembers) {
                                            if (Array.isArray(payment.godGroup.groupMembers.participants)) {
                                                participants = payment.godGroup.groupMembers.participants;
                                            } else if (payment.godGroup.groupMembers.participants && 
                                                      Array.isArray(payment.godGroup.groupMembers.participants.memberID)) {
                                                // Handle aggregation result structure
                                                participants = payment.godGroup.groupMembers.participants.memberID;
                                            } else {
                                                participants = [];
                                            }
                                        }
                                        const eligibleMembers = getEligibleMembers(payment, participants);
                                        
                                        return (
                                            <tr key={payment._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentTypeColor(payment.paymentType)}`}>
                                                        {formatPaymentType(payment.paymentType)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {payment.godMember ? (
                                                        <div>
                                                            <div className="font-medium">{payment.godMember.fullName}</div>
                                                        </div>
                                                    ) : payment.godGroup ? (
                                                        <div>
                                                            <div className="font-medium">{payment.godGroup.groupName}</div>
                                                            {eligibleMembers.map((participant, index) => {
                                                                const fullName = participant.memberID?.fullName || 'No name found';
                                                                const joinDate = participant.joinDate ? new Date(participant.joinDate).toLocaleDateString() : '';
                                                                const removeDate = participant.removeDate ? new Date(participant.removeDate).toLocaleDateString() : '';
                                                                
                                                                return (
                                                                    <div key={index} className="text-sm text-gray-600">
                                                                        {removeDate ? 
                                                                            `${fullName} - X ${removeDate}` : 
                                                                            `${fullName} (${joinDate})`
                                                                        }
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {formatAmount(payment.paymentAmount)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {formatDate(payment.paymentDate)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {formatDate(payment.renewalDate)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => handleView(payment)}
                                                        className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                        title="View Details"
                                                    >
                                                        <FiEye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(payment)}
                                                        className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                        title="Edit Payment"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(payment)}
                                                        className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                        title="Delete Payment"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination - only show if not searching */}
                        {!hasSearched && (
                            <Pagination 
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        )}

                        {/* View Modal */}
                        {showViewModal && selectedPayment && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
                                <div className="relative top-20 mx-auto p-5 border w-[800px] shadow-lg rounded-md bg-white">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-medium">Payment Details</h3>
                                        <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700">
                                            ×
                                        </button>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <span className="font-semibold">Payment Type:</span>
                                            <span className={`ml-2 px-2 py-1 text-sm font-semibold rounded-full ${getPaymentTypeColor(selectedPayment.paymentType)}`}>
                                                {formatPaymentType(selectedPayment.paymentType)}
                                            </span>
                                        </div>
                                        {selectedPayment.godMember && (
                                            <div>
                                                <h4 className="font-semibold mb-2">Member Information</h4>
                                                <div className="pl-4 space-y-1">
                                                    <p>Name: {selectedPayment.godMember.fullName}</p>
                                                    <p>God ID: {selectedPayment.godMember.godID}</p>
                                                    <p>Web ID: {selectedPayment.godMember.webID}</p>
                                                </div>
                                            </div>
                                        )}
                                        {selectedPayment.godGroup && (
                                            <div>
                                                <h4 className="font-semibold mb-2">Group Information</h4>
                                                <div className="pl-4 space-y-1">
                                                    <p>Group Name: {selectedPayment.godGroup.groupName}</p>
                                                    <p>Handler: {selectedPayment.godGroup.groupMembers?.handler?.fullName || 'N/A'}</p>
                                                    <div className="mt-2">
                                                        <p className="font-medium">Participants:</p>
                                                        {(() => {
                                                            // Safely access participants data
                                                            let participants = [];
                                                            if (selectedPayment.godGroup.groupMembers?.participants) {
                                                                if (Array.isArray(selectedPayment.godGroup.groupMembers.participants)) {
                                                                    participants = selectedPayment.godGroup.groupMembers.participants;
                                                                } else if (Array.isArray(selectedPayment.godGroup.groupMembers.participants.memberID)) {
                                                                    participants = selectedPayment.godGroup.groupMembers.participants.memberID;
                                                                }
                                                            }
                                                            
                                                            if (participants.length === 0) {
                                                                return <div className="pl-4 text-gray-500 italic">No participants found</div>;
                                                            }
                                                            
                                                            return participants.map((participant, index) => {
                                                                const fullName = participant.memberID?.fullName || 'No name found';
                                                                const joinDate = participant.joinDate ? new Date(participant.joinDate).toLocaleDateString() : '';
                                                                const removeDate = participant.removeDate ? new Date(participant.removeDate).toLocaleDateString() : '';
                                                                
                                                                return (
                                                                    <div key={index} className="pl-4 mt-1 flex items-center gap-2">
                                                                        {participant.removeDate && (
                                                                            <FiX className="text-red-500 w-4 h-4" title="Removed" />
                                                                        )}
                                                                        <span className={participant.removeDate ? 'line-through text-gray-400' : ''}>
                                                                            {removeDate ? 
                                                                                `${fullName} - X ${removeDate}` : 
                                                                                `${fullName} (${joinDate})`
                                                                            }
                                                                        </span>
                                                                        {participant.remarks && (
                                                                            <span className="text-gray-500 italic">
                                                                                ({participant.remarks})
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <h4 className="font-semibold mb-2">Payment Information</h4>
                                            <div className="pl-4 space-y-1">
                                                <p>Amount: {formatAmount(selectedPayment.paymentAmount)}</p>
                                                <p>Payment Date: {formatDate(selectedPayment.paymentDate)}</p>
                                                {selectedPayment.paymentType !== 'misc_fees' && (
                                                <p>Renewal Date: {formatDate(selectedPayment.renewalDate)}</p>
                                                )}
                                                <p>Remarks: {selectedPayment.paymentRemarks || 'No remarks'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
};

export default PaymentPage; 